import { useEffect } from 'react';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import SectionHeader from '../components/common/SectionHeader';

const buildAreas = [
  {
    title: 'Software Products',
    description:
      'SaaS tools, workflow systems, dashboards, admin panels, automation, and structured software planning products.',
  },
  {
    title: 'Education Platforms',
    description:
      'Learning resources, tutoring ecosystems, structured academic content, tests, and student-focused digital learning systems.',
  },
  {
    title: 'Business Websites & Portals',
    description:
      'Professional websites, lead-generation systems, CMS-driven pages, project showcases, and operational portals.',
  },
  {
    title: 'Internal Digital Systems',
    description:
      'Requirement tools, proposal systems, admin workflows, backups, SEO systems, analytics, and business process automation.',
  },
];

const operatingProjects = [
  {
    name: 'DevX Factor',
    category: 'SaaS / Software Planning',
    url: 'https://devxfactor.com',
    description:
      'DevX Factor helps founders, developers, students, and agencies turn project ideas into structured requirements, proposal-ready PDFs, invoices, and AI-powered software blueprints.',
    capabilities: ['Requirement planning', 'Proposal PDFs', 'AI software blueprints'],
  },
  {
    name: 'SatyamCode',
    category: 'Full-Stack Development / Resources',
    url: 'https://satyamcode.com',
    description:
      'SatyamCode is a freelance development and learning ecosystem showcasing full-stack web development, resources, project systems, and practical MERN application workflows.',
    capabilities: ['MERN workflows', 'Project systems', 'Developer resources'],
  },
  {
    name: 'PhysicsFactor',
    category: 'Education Platform',
    url: 'https://physicsfactor.com',
    description:
      'PhysicsFactor is focused on systematic physics learning through concise theory, concept-building questions, progressive difficulty levels, and structured chapter-wise testing.',
    capabilities: ['Concept learning', 'Practice questions', 'Chapter-wise tests'],
  },
  {
    name: 'SatyamSir',
    category: 'Tutoring Brand',
    url: 'https://satyamsir.com',
    description:
      'SatyamSir is the personal education brand for physics mentoring, tutoring support, student guidance, and academic resources.',
    capabilities: ['Physics mentoring', 'Student support', 'Academic resources'],
  },
];

const missionPoints = [
  'Build useful software products that simplify workflows for businesses, developers, and agencies.',
  'Develop education platforms that make learning more structured, accessible, and result-oriented.',
  'Create digital systems that combine product design, automation, content, and operations.',
  'Grow focused online businesses with long-term maintainability and clear execution.',
  'Build products with real users, real workflows, and measurable business value.',
];

const scaleCards = [
  {
    title: 'Multi-project portfolio',
    description:
      'The company is not dependent on a single product. It operates across SaaS, education, development services, and learning platforms.',
  },
  {
    title: 'Shared technology foundation',
    description:
      'Products can reuse common infrastructure, full-stack architecture, admin systems, CMS workflows, SEO systems, and automation pipelines.',
  },
  {
    title: 'Founder-led product execution',
    description:
      'Technical ownership and product decisions stay close to execution, reducing dependency on large external teams.',
  },
  {
    title: 'Revenue-focused direction',
    description:
      'Projects are designed around practical use cases, lead generation, education value, SaaS workflows, and long-term digital business potential.',
  },
  {
    title: 'Low operational overhead',
    description:
      'Digital-first operations allow the company to test, improve, and scale products without heavy physical infrastructure.',
  },
  {
    title: 'Long-term brand ecosystem',
    description:
      'Each project strengthens the larger FactorStreet ecosystem through content, services, trust, and technology capability.',
  },
];

const timelineItems = [
  {
    year: '2019',
    text: "Idea and early education operations started under Satyam Kumar's leadership.",
  },
  {
    year: '2020',
    text: 'Factor Street E-commerce Services Pvt. Ltd. was incorporated.',
  },
  {
    year: '2021-2023',
    text: 'Education-focused digital initiatives and structured learning resources continued to evolve.',
  },
  {
    year: '2024-2025',
    text: 'Stronger focus shifted toward software products, full-stack systems, SaaS workflows, and digital platform development.',
  },
  {
    year: 'Today',
    text: 'FactorStreet operates a focused portfolio across DevX Factor, SatyamCode, PhysicsFactor, and SatyamSir.',
  },
  {
    year: 'Future',
    text: 'The company aims to grow its product ecosystem with scalable technology, education platforms, and business-focused digital systems.',
  },
];

const heroBadges = ['Technology Products', 'Education Platforms', 'SaaS Systems', 'Digital Operations'];
const trustPoints = [
  'Founder-led execution',
  'Product-first mindset',
  'Technology + education focus',
  'Practical revenue-oriented systems',
];

function About() {
  useEffect(() => {
    document.title = 'About FactorStreet | Technology & Education Company';

    const metaDescription =
      document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute(
      'content',
      'FactorStreet is a founder-led company building digital products, education platforms, SaaS systems, and practical business technology initiatives across DevX Factor, SatyamCode, PhysicsFactor, and SatyamSir.'
    );

    if (!metaDescription.parentNode) {
      document.head.appendChild(metaDescription);
    }
  }, []);

  return (
    <main className="about-page">
      <section className="about-hero">
        <div className="container about-hero__grid">
          <div className="about-hero__copy">
            <p className="eyebrow">About FactorStreet</p>
            <h1>Building digital businesses across technology and education.</h1>
            <p>
              Factor Street E-commerce Services Pvt. Ltd. is a founder-led company focused on
              creating practical digital products, education platforms, software systems, and
              scalable online business initiatives.
            </p>
            <div className="about-hero__badges" aria-label="Company focus areas">
              {heroBadges.map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </div>
          </div>

          <Card className="about-hero__panel">
            <span className="about-panel__label">Operating focus</span>
            <strong>Technology, education, systems, and scalable digital execution.</strong>
            <p>
              FactorStreet builds practical products and platforms with a long-term operating
              mindset.
            </p>
          </Card>
        </div>
      </section>

      <section className="section about-overview-section">
        <div className="container about-overview-grid">
          <div className="about-overview-copy">
            <p className="eyebrow">Who we are</p>
            <h2>FactorStreet is a product-building parent company.</h2>
            <p>
              FactorStreet is the parent company behind multiple digital initiatives built around
              software, education, and practical online business systems. The company focuses on
              building and operating products that solve real workflow, learning, and digital
              execution problems.
            </p>
            <p>
              Founded by Satyam Kumar, FactorStreet combines education experience, product
              thinking, full-stack development capability, and long-term business execution into one
              focused operating company.
            </p>
          </div>

          <Card className="about-trust-card">
            <span>Company strengths</span>
            <ul>
              {trustPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      <section className="section about-build-section">
        <div className="container">
          <SectionHeader
            eyebrow="What FactorStreet Builds"
            title="What we build"
            description="The company develops digital systems where software, education, content, and operations work together."
          />
          <div className="about-build-grid">
            {buildAreas.map((area) => (
              <Card className="about-feature-card" key={area.title}>
                <h3>{area.title}</h3>
                <p>{area.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="section about-portfolio-section" id="about-projects">
        <div className="container">
          <SectionHeader
            eyebrow="Operating Projects"
            title="Current operating projects"
            description="FactorStreet currently operates and supports a focused portfolio of digital and education-driven projects."
          />
          <div className="about-project-grid">
            {operatingProjects.map((project) => (
              <Card className="about-project-card" key={project.name}>
                <div className="about-project-card__topline">
                  <span>{project.category}</span>
                  <strong>ACTIVE</strong>
                </div>
                <h3>{project.name}</h3>
                <p>{project.description}</p>
                <ul>
                  {project.capabilities.map((capability) => (
                    <li key={capability}>{capability}</li>
                  ))}
                </ul>
                <Button href={project.url} variant="secondary">
                  Visit Website
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="section about-vision-section">
        <div className="container about-vision-grid">
          <Card className="about-vision-card">
            <p className="eyebrow">Vision</p>
            <h2>Build a trusted technology and education company.</h2>
            <p>
              To build a trusted technology and education company that creates scalable digital
              products, practical learning platforms, and business systems that solve real problems.
            </p>
          </Card>

          <Card className="about-vision-card about-vision-card--mission">
            <p className="eyebrow">Mission</p>
            <h2>Execution with practical value.</h2>
            <ul>
              {missionPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      <section className="section about-scale-section">
        <div className="container">
          <SectionHeader
            eyebrow="Business Model"
            title="Why FactorStreet is built to scale"
            description="FactorStreet is designed as an operating company, not just a single website. Each project supports a focused audience while sharing technology, systems, and operational learning across the company."
            align="center"
          />
          <div className="about-scale-grid">
            {scaleCards.map((card) => (
              <Card className="about-scale-card" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="section about-timeline-section">
        <div className="container">
          <SectionHeader
            eyebrow="Journey"
            title="Our journey"
            description="A focused path from education-led beginnings toward software products, structured platforms, and digital business systems."
          />
          <div className="about-timeline">
            {timelineItems.map((item) => (
              <div className="about-timeline__item" key={item.year}>
                <span>{item.year}</span>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section about-leadership-section">
        <div className="container">
          <SectionHeader eyebrow="Leadership" title="Leadership" />
          <div className="about-leadership-grid">
            <Card className="about-founder-card">
              <div className="avatar-placeholder avatar-placeholder--large">SK</div>
              <div>
                <span>Founder & CEO</span>
                <h3>Satyam Kumar</h3>
                <p>
                  Satyam Kumar leads FactorStreet with a background in education, technology,
                  product thinking, and full-stack development. His experience as a physics educator
                  and software builder shapes the company's approach: understand the fundamentals,
                  solve real problems, and build systems that can grow over time.
                </p>
                <Button to="/about-ceo" variant="secondary">
                  About CEO
                </Button>
              </div>
            </Card>

            <Card className="about-approach-card">
              <span>Company approach</span>
              <h3>Fundamentals first. Systems second. Scale with clarity.</h3>
              <p>
                FactorStreet keeps product thinking close to implementation so each initiative can
                improve through practical use, measured feedback, and maintainable systems.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <section className="section about-cta-section">
        <div className="container">
          <div className="about-cta">
            <div>
              <p className="eyebrow">Collaborate</p>
              <h2>Want to collaborate with FactorStreet?</h2>
              <p>
                Whether it is a technology product, education initiative, digital platform, or
                business collaboration, we are open to meaningful conversations.
              </p>
            </div>
            <div className="about-cta__actions">
              <Button to="/contact">Contact Us</Button>
              <a className="btn btn--secondary" href="#about-projects">
                Explore Projects
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default About;
